// Lifted Monthly Box — public page.
//
// Phase 1 ships the full public experience (plans, features, how it works,
// FAQ) with a waitlist signup. Phase 2 will swap the "Join Waitlist" button
// for real Square Subscription checkout once backend subscriptions are
// wired. Per owner directive: NO fake subscribe buttons until billing is
// verified end-to-end in Square sandbox.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ChevronRight, Package, ShieldCheck, Truck, Repeat, Mail } from "lucide-react";
import api from "@/lib/api";
import PageMeta from "@/components/PageMeta";
import { SubscriptionEnroll, BoxTermsPanel } from "@/components/monthlybox/SubscriptionEnroll";


const FAQS = [
  { q: "Does the box contents change each month?",
    a: "Yes — each month features a new curated assortment of Lifted apparel, accessories, and lifestyle pieces. Example products displayed on this page vary month to month." },
  { q: "How does billing work?",
    a: "Billing is monthly. You'll see the exact recurring charge and renewal date before you confirm your subscription, and you can cancel anytime before the next renewal from your account." },
  { q: "How do I cancel?",
    a: "Cancel from My Monthly Box in your account. Future charges stop and months you've already paid for stay active. The full cancellation policy is shown before you subscribe." },
  { q: "What is the Lift-Off Box ($40/mo)?",
    a: "The $40 Lift-Off Box bundles a monthly curated box with a Lift-Off affiliate membership so you can earn commission on every referral while receiving your box." },
  { q: "I'm already a Lifted affiliate — do I get a complimentary box?",
    a: "Eligible active affiliates on record keep complimentary monthly boxes. Eligibility is set by Lifted administrators and visible on your account; it is never self-awarded." },
  { q: "Will joining the box change my affiliate rank?",
    a: "No. Box upgrades are separate from affiliate rank progression. Your earned rank stays intact." },
];

export default function LiftedMonthlyBox() {
  const [config, setConfig] = useState(null);
  const [eligibility, setEligibility] = useState(null);
  const [examples, setExamples] = useState({ items: [], disclosure: "" });
  const [selectedPlan, setSelectedPlan] = useState("liftoff");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [status, setStatus] = useState({ kind: "idle", msg: "" });

  useEffect(() => {
    api.get("/monthly-box/config").then((r) => setConfig(r.data)).catch(() => setConfig(null));
    api.get("/monthly-box/eligibility").then((r) => setEligibility(r.data)).catch(() => setEligibility(null));
    api.get("/monthly-box/examples").then((r) => setExamples({
      items: r.data?.examples || [],
      disclosure: r.data?.disclosure || "",
    })).catch(() => setExamples({ items: [], disclosure: "" }));
  }, []);

  const plans = config?.plans || [];
  const subscriptionsEnabled = !!config?.subscriptions_enabled;

  async function submitWaitlist(e) {
    e.preventDefault();
    if (!email) { setStatus({ kind: "err", msg: "Email is required." }); return; }
    setStatus({ kind: "loading", msg: "" });
    try {
      const { data } = await api.post("/monthly-box/waitlist", {
        email, plan_id: selectedPlan, name: name || null,
      });
      setStatus({
        kind: "ok",
        msg: data?.already_on_list
          ? "You're already on the list — we updated your preferred plan."
          : "You're on the list! We'll email you when subscriptions open.",
      });
      setName(""); setEmail("");
    } catch (err) {
      setStatus({ kind: "err",
        msg: err?.response?.data?.detail || "Could not join the waitlist. Try again shortly." });
    }
  }

  return (
    <div className="min-h-screen bg-[#04070a] text-white" data-testid="monthly-box-page">
      <PageMeta
        title="Lifted Monthly Box — Curated apparel + accessories"
        description="A monthly curated Lifted box: apparel, accessories, and an affiliate membership option. Starting at $40/month."
        path="/lifted-monthly-box"
      />
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 opacity-30 pointer-events-none"
             style={{ background: "radial-gradient(circle at 20% 30%, rgba(132,255,55,0.15), transparent 55%)" }} />
        <div className="container mx-auto px-4 py-14 lg:py-20 relative">
          <div className="eyebrow text-lifted-green mb-3 flex items-center gap-2">
            <Package className="h-3 w-3" /> Monthly Subscription
          </div>
          <h1 className="font-anton uppercase text-4xl sm:text-5xl lg:text-6xl text-white max-w-3xl leading-none">
            Lifted <span className="italic text-lifted-green">Monthly</span> Box
          </h1>
          <p className="text-white/70 mt-4 max-w-2xl text-lg">
            A curated monthly drop of apparel, accessories, and lifestyle pieces from the Lifted catalog.
            One connected box experience — add an affiliate membership and earn while you receive.
          </p>
          {eligibility?.complimentary && (
            <div className="mt-5 inline-flex items-center gap-2 px-3 py-2 rounded border border-lifted-green/50 bg-lifted-green/10 text-lifted-green text-xs font-bebas tracking-[0.2em] uppercase"
                 data-testid="complimentary-badge">
              <Check className="h-4 w-4" /> Complimentary member — your box ships monthly on us
            </div>
          )}
        </div>
      </section>

      {/* WHAT COULD BE INSIDE — real catalog imagery, labeled honestly. */}
      {examples.items.length > 0 && (
        <section className="py-14 lg:py-16 bg-black/30 border-y border-white/5"
                 data-testid="box-examples-section">
          <div className="container mx-auto px-4">
            <div className="text-center mb-8">
              <div className="eyebrow text-lifted-green">Preview</div>
              <h2 className="font-anton uppercase text-3xl lg:text-4xl text-white mt-2">
                What Could Be <span className="italic text-lifted-green">Inside</span> Your Lifted Box?
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="box-examples-grid">
              {examples.items.map((it) => (
                <Link key={it.handle} to={it.product_url}
                  data-testid={`box-example-${it.handle}`}
                  className="group block">
                  <div className="aspect-square overflow-hidden rounded-lg bg-black/40 border border-white/10 group-hover:border-lifted-green/50 transition-colors">
                    <img src={it.image_url} alt={it.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                  </div>
                  <div className="mt-2 text-xs text-white truncate">{it.title}</div>
                  {it.price != null && (
                    <div className="text-[10px] text-lifted-green font-bebas tracking-wider">
                      ${Number(it.price).toFixed(2)}
                    </div>
                  )}
                </Link>
              ))}
            </div>
            <p className="text-center text-xs text-white/50 mt-6 italic max-w-2xl mx-auto"
               data-testid="box-examples-disclosure">
              {examples.disclosure}
            </p>
          </div>
        </section>
      )}

      {/* PLANS */}
      <section className="py-14 lg:py-20" data-testid="plans-section">  <div className="container mx-auto px-4">
          <h2 className="font-anton uppercase text-3xl lg:text-4xl text-center text-white">
            Choose <span className="italic text-lifted-green">Your</span> Box
          </h2>
          <p className="text-center text-white/60 text-sm mt-2">
            Example products shown; monthly contents vary. Full billing, shipping and cancellation terms are shown before you enroll.
          </p>
          <p className="text-[11px] text-white/45 mt-2 text-center max-w-2xl mx-auto" data-testid="box-rank-note">
            Box plans are purchases. Affiliate ranks are separate: Orbit, Galaxy and Cosmos ranks are earned at 10, 100 and 1,000 active referrals — buying a box never changes your rank.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10" data-testid="box-plans-grid">
            {plans.map((p) => {
              const active = selectedPlan === p.id;
              return (
                <div
                  key={p.id}
                  data-testid={`plan-${p.id}`}
                  className={`glass-panel p-6 flex flex-col border transition-all cursor-pointer ${
                    active ? "border-lifted-green/60 -translate-y-1" : "border-white/10 hover:border-white/30"
                  }`}
                  onClick={() => setSelectedPlan(p.id)}
                >
                  <span className="self-start text-[10px] font-bebas tracking-[0.22em] uppercase text-lifted-green mb-2" data-testid={`plan-tier-${p.id}`}>
                    Tier {p.order} · {p.tier_label}
                  </span>
                  {p.includes_affiliate && (
                    <span className="self-start text-[9px] font-bebas tracking-[0.2em] uppercase text-black bg-lifted-green px-2 py-1 rounded mb-3">
                      Includes Lift-Off membership
                    </span>
                  )}
                  <h3 className="font-anton uppercase text-2xl text-white">{p.name}</h3>
                  <p className="text-xs text-lifted-dim mt-1">{p.tagline}</p>
                  <div className="font-anton text-4xl text-lifted-green mt-5">
                    ${(p.price_cents / 100).toFixed(0)}
                    <span className="text-sm text-white/60 font-sans ml-1">/month</span>
                  </div>
                  <ul className="mt-5 space-y-2 flex-1">
                    {(p.features || []).map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-white/80">
                        <Check className="h-4 w-4 text-lifted-green shrink-0 mt-0.5" /> {f}
                      </li>
                    ))}
                  </ul>
                  <button
                    data-testid={`plan-${p.id}-select`}
                    className={`mt-5 w-full py-2.5 rounded font-bebas tracking-[0.18em] uppercase text-xs transition-colors ${
                      active ? "bg-lifted-green text-black" : "border border-white/20 text-white hover:border-lifted-green"
                    }`}
                    onClick={() => setSelectedPlan(p.id)}
                  >
                    {active ? "Selected" : "Select plan"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-14 bg-black/30 border-y border-white/5">
        <div className="container mx-auto px-4">
          <h2 className="font-anton uppercase text-3xl text-center text-white">
            How It <span className="italic text-lifted-green">Works</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mt-10">
            {[
              { icon: Package, title: "Pick your box",   copy: "Choose Affiliate, Upgraded, or Premium." },
              { icon: Repeat, title: "Billed monthly",   copy: "Recurring charge shown and agreed before enrolling." },
              { icon: Truck,  title: "Shipped to you",   copy: "Fresh assortment delivered each month." },
              { icon: ShieldCheck, title: "Manage online", copy: "View or cancel your plan from your account." },
            ].map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="glass-panel p-5" data-testid={`how-it-works-${i}`}>
                  <Icon className="h-6 w-6 text-lifted-green mb-3" />
                  <div className="font-bebas uppercase tracking-[0.18em] text-sm text-white">{s.title}</div>
                  <p className="text-xs text-white/60 mt-1">{s.copy}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* AFFILIATE EXPLAINER */}
      <section className="py-14">
        <div className="container mx-auto px-4 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="eyebrow text-lifted-green mb-2">Affiliate Membership</div>
            <h2 className="font-anton uppercase text-3xl lg:text-4xl text-white">
              The $40 Plan Pays You <span className="italic text-lifted-green">Back</span>
            </h2>
            <p className="text-white/70 mt-4 text-sm">
              The $40 Lift-Off Box bundles your monthly box with a Lift-Off affiliate membership.
              Earn commission every time someone purchases through your referral link, while receiving
              your curated box each month. Existing affiliates with complimentary status keep their perk.
            </p>
            <Link to="/affiliate-zone" data-testid="box-affiliate-link"
              className="btn-ghost mt-5 inline-flex items-center gap-1">
              Learn about the affiliate program <ChevronRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="glass-panel p-6" data-testid="affiliate-compat-panel">
            <div className="eyebrow text-white/60">For existing affiliates</div>
            <h3 className="font-bebas uppercase tracking-[0.18em] text-xl text-white mt-2">
              You're Not Double-Billed
            </h3>
            <p className="text-sm text-white/70 mt-3">
              If you're already a Lifted affiliate on complimentary status, your monthly box continues
              to ship on us — no action required. If you'd like to upgrade to an Upgraded or Premium
              box, you can add the delta from your account.
            </p>
            {eligibility && !eligibility.complimentary && (
              <div className="mt-3 text-xs text-lifted-dim" data-testid="eligibility-not-set">
                Your account is not currently on complimentary status.
              </div>
            )}
          </div>
        </div>
      </section>

      {/* WAITLIST / SUBSCRIBE */}
      <section className="py-14 bg-black/40 border-y border-white/5" data-testid="box-signup-section">
        <div className="container mx-auto px-4 max-w-xl">
          {config?.shipping_terms && <BoxTermsPanel terms={config.shipping_terms} />}
          {subscriptionsEnabled ? (
            <SubscriptionEnroll provider={config?.provider} planKey={selectedPlan} planLabel={plans.find((p) => p.id === selectedPlan)?.name} />
          ) : (
          <div className="text-center">
          <Mail className="h-8 w-8 text-lifted-green mx-auto mb-3" />
          <h2 className="font-anton uppercase text-3xl text-white">Join The Waitlist</h2>
          <p className="text-white/60 text-sm mt-2" data-testid="enrollment-closed-msg">
            Paid enrollment isn't open yet — no one is being charged. Reserve your spot and we'll email you when it opens.
          </p>
          <form onSubmit={submitWaitlist} className="mt-6 space-y-3 text-left" data-testid="box-waitlist-form">
            <label className="block text-xs font-bebas tracking-[0.18em] uppercase text-white/70">Name (optional)</label>
            <input value={name} onChange={(e) => setName(e.target.value)}
              className="input-lifted w-full" data-testid="waitlist-name" />
            <label className="block text-xs font-bebas tracking-[0.18em] uppercase text-white/70">Email</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="input-lifted w-full" data-testid="waitlist-email" />
            <div className="text-xs text-white/60 pt-1">
              Selected plan: <span className="text-lifted-green font-bebas uppercase tracking-wider">{selectedPlan}</span>
            </div>
            <button type="submit" disabled={status.kind === "loading"}
              className="btn-lifted w-full mt-2 disabled:opacity-50" data-testid="waitlist-submit">
              {status.kind === "loading" ? "Joining…" : "Join waitlist"}
            </button>
            {status.kind === "ok" && (
              <div className="text-sm text-lifted-green pt-2" data-testid="waitlist-success">{status.msg}</div>
            )}
            {status.kind === "err" && (
              <div className="text-sm text-rose-300 pt-2" data-testid="waitlist-error">{status.msg}</div>
            )}
          </form>
          </div>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14" data-testid="faq-section">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="font-anton uppercase text-3xl text-center text-white">
            Frequently <span className="italic text-lifted-green">Asked</span>
          </h2>
          <div className="mt-8 space-y-3">
            {FAQS.map((f, i) => (
              <details key={i} className="glass-panel p-4 group" data-testid={`faq-${i}`}>
                <summary className="cursor-pointer list-none flex justify-between items-center">
                  <span className="font-bebas uppercase tracking-[0.18em] text-sm text-white">{f.q}</span>
                  <ChevronRight className="h-4 w-4 text-lifted-green transition-transform group-open:rotate-90" />
                </summary>
                <p className="text-sm text-white/70 mt-3">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
